<template>
  <main>
    <div class="cp-pairing__container">
      <h2 class="cp-pairing__title" v-html="title"></h2>
      <div class="cp-pairing__steps">
        <div class="cp-pairing__step">
          <div class="cp-pairing__step__indicator-container">
            <div class="cp-pairing__step__number-filled" :style="{ backgroundColor: theme.primaryColor }">1</div>
            <div class="cp-pairing__step__line"></div>
          </div>
          <div class="cp-pairing__step__content">
            <p class="cp-pairing__step__title">{{ $t('chargepointPairing.stepTitle1') }}</p>
            <p class="cp-pairing__step__description">{{ $t('chargepointPairing.step1') }}</p>
            <div id="cp-pairing__code__container">
              <p class="cp-pairing__code"
                  :style="{
                  color: theme.primaryColor,
                  borderColor: theme.primaryColor
                }"
                >
                {{ pairingCode }}
              </p>
              <button
                ref="copyToClipboard"
                :style="{
                  color: copyCodeColor,
                  borderColor: theme.primaryColor,
                }"
                class="cp-pairing__code__button"
                v-clipboard:copy="pairingCode"
                v-clipboard:success="onCopy"
                v-clipboard:error="onCopyError"
              >
                {{ pairingBtnTxt }}
              </button>
            </div>
            <div class="buffer-line"></div>
          </div>
        </div>
        <div class="cp-pairing__step">
          <div class="cp-pairing__step__indicator-container">
            <div
              :class="codeCopied ? 'cp-pairing__step__number-filled' : 'cp-pairing__step__number-empty'"
              :style="{
                color: !codeCopied ? theme.primaryColor : '',
                borderColor: !codeCopied ? theme.primaryColor : '',
                backgroundColor: codeCopied ? theme.primaryColor : ''
              }"
            >2</div>
            <div class="cp-pairing__step__line"></div>
          </div>
          <div class="cp-pairing__step__content">
            <p class="cp-pairing__step__title">{{ $t('chargepointPairing.stepTitle2') }}</p>
            <p class="cp-pairing__step__description" v-html="$t('chargepointPairing.step2')"></p>
            <div>
              <button
                ref="openConnectionsPage"
                :class="codeCopied && !hasErrors ? 'chargepoint-link-colored' : 'chargepoint-link-grey'"
                :style="{
                  color: codeCopied && !hasErrors ? theme.primaryColor : '',
                  borderColor: codeCopied && !hasErrors ? theme.primaryColor : '',
                }"
                :disabled="!codeCopied || hasErrors"
                @click="chargepointLogin"
              >
                {{ connectBtnTxt }}
              </button>
            </div>
            <div class="cp-pairing__step__support-info">
              <div
                class="cp-pairing__step__support-info__toggle"
                :style="{
                  color: theme.primaryColor,
                  cursor: codeCopied ? 'pointer' : 'not-allowed'
                }">
                <p
                  class="cp-pairing__step__support-info__text-toggle"
                  :style="{
                    color: supportInfoTextToggleColor,
                  }"
                  :disabled="!codeCopied"
                  @click="codeCopied ? toggleSupportDropdown() : null"
                  >
                    {{ $t('chargepointPairing.supportInfoTextPrompt') }}
                </p>
                <vp-icon
                  id="support-info__icon"
                  :icon="showSupportDropdown ? 'chevron-down' : 'chevron-up'"
                  :style="{
                    color: supportInfoTextToggleColor,
                  }"
                  @click="codeCopied ? toggleSupportDropdown() : null"
                  ></vp-icon>
              </div>
              <div
                v-show="showSupportDropdown"
                class="cp-pairing__step__support-info__content"
                :style="{ backgroundColor: supportInfoBgColor }"
              >
                <div
                  class="cp-pairing__step__support-info__icon__container"
                >
                  <VpIcon
                    icon="exclamation-circle"
                    id="support-info__icon__attention"
                    :style="{ color: theme.primaryColor }"
                  />
                </div>
                <div class="cp-pairing__support-info__text">
                  <p v-html="supportContentHtml" :style="{ color: theme.primaryColor }"></p>
                  <p>
                    <span :style="{ color: theme.primaryColor }">{{ $t('chargepointPairing.supportContent2') }}</span>
                    <span
                      class="cp-pairing__step__support-modal__text-toggle"
                      :style="{ color: theme.primaryColor }"
                      @click="showSupportModal = true"
                    >
                      {{ $t('chargepointPairing.supportContent3') }}
                    </span>
                    <span :style="{ color: theme.primaryColor }">{{ $t('chargepointPairing.supportContent4') }}</span>
                  </p>
                </div>
              </div>
            </div>
            <div class="buffer-line"></div>
          </div>
        </div>
        <div class="cp-pairing__step">
          <div class="cp-pairing__step__indicator-container">
            <div
              :class="{
                'cp-pairing__step__number-grey': !loginClicked,
                'cp-pairing__step__number-empty': loginClicked,
              }"
              :style="loginClicked ? { color: theme.primaryColor, borderColor: theme.primaryColor } : {}"
            >3</div>
          </div>
          <div class="cp-pairing__step__content">
            <p class="cp-pairing__step__title">{{ $t('chargepointPairing.stepTitle3') }}</p>
            <p class="cp-pairing__step__description">{{ $t('chargepointPairing.step3') }}</p>
            <button
              ref="openConnectionsPage"
              :class="loginClicked && !hasErrors ? 'submit-button-colored' : 'submit-button-grey'"
              :disabled="!loginClicked || hasErrors"
              @click="handleSubmit"
            >
              {{ $t('chargepointPairing.submitButton') }}
            </button>
          </div>
        </div>
      </div>
    </div>
    <ChargePointSupportModal
      :visible="showSupportModal"
      :theme="theme"
      :support-email="supportEmail"
      :connection-link="connectionLink"
      @close="showSupportModal = false"
    />
  </main>
</template>

<script>
import ChargePointSupportModal from '../Modal/ChargePointSupportModal.vue';
import defaultTheme from '../../styles/defaultTheme';
import vp from '../../services/api/vp';

export default {
  name: 'question-chargepoint-pairing-code',
  components: {
    ChargePointSupportModal,
  },
  props: {
    question: Object,
    stepValues: Object,
    parentRefs: Object,
  },
  inject: {
    account: 'account',
    customer: 'customer',
    program: 'program',
    theme: { default: defaultTheme },
  },
  data() {
    return {
      codeCopied: false,
      showSupportDropdown: false,
      showSupportModal: false,
      loginClicked: false,
      isMobile: false,
      connectionsUrl: '',
      connectBtnTxt: this.$i18n.t('chargepointPairing.connectionButton'),
      pairingCode: '00000',
      pairingBtnTxt: this.$i18n.t('chargepointPairing.pairingCodeButton'),
      partnerToken: '',
      supportEmail: 'support@virtual-peaker.com',
      deviceData: {},
    };
  },
  watch: {
    isReady: {
      immediate: true,
      handler(current, prev) {
        if (!prev && current) {
          this.fetchPairingCode();
        }
      },
    },
  },
  computed: {
    title() {
      return this.isMobile ? this.$i18n.t('chargepointPairing.headlineMobile') : this.$i18n.t('chargepointPairing.headline');
    },
    houseId() {
      return this.account ? this.account.houseId : null;
    },
    programName() {
      return this.program ? this.program.programName : null;
    },
    providerId() {
      return this.customer ? this.customer.id : null;
    },
    mfg() {
      return this.question.mfg;
    },
    missingFields() {
      const requiredFields = ['mfg', 'programName', 'providerId'];
      const missing = requiredFields.filter(fld => !this[fld]);
      return missing;
    },
    isReady() {
      return !this.missingFields.length;
    },
    copyCodeColor() {
      if (this.codeCopied) {
        return '#4CC68B';
      }
      return this.theme.primaryColor;
    },
    supportInfoTextToggleColor() {
      if (this.codeCopied && !this.showSupportDropdown) {
        return '#4F4F4F';
      }
      if (this.codeCopied && this.showSupportDropdown) {
        return this.theme.primaryColor;
      }
      return '#AFAFAF';
    },
    supportContentHtml() {
      const color = this.theme.primaryColor;
      const content = this.$t('chargepointPairing.supportContent1');
      return content.replace(
        '{{ partnerToken }}',
        `<span class="partner-token-bold" style="color: ${color};">${this.partnerToken}</span>`,
      );
    },
    supportInfoBgColor() {
      return this.hexToRgba(this.theme.primaryColor, 0.08);
    },
    connectionLink() {
      return this.$t('chargepointPairing.supportArticleLinkUrl');
    },
    hasErrors() {
      return this.errors?.items && this.errors.items.length > 0;
    },
  },
  async mounted() {
    this.toggleAddDeviceBtnDisplay('none');
    this.isMobile = window.matchMedia('(max-width: 576px)').matches;
    this.supportEmail = this.customer.supportEmail;
    try {
      await this.fetchPairingCode();
    } catch (err) {
      this.$router.push('/error');
    }
  },
  methods: {
    toggleAddDeviceBtnDisplay(set) {
      const submitBtns = [...document.getElementsByClassName('pull-right question-button')];
      const [addDeviceBtn] = submitBtns.filter(el => el.innerText.includes('Add Device') || el.innerText.includes('Agregar un dispositivo'));
      if (addDeviceBtn !== undefined) {
        addDeviceBtn.style.display = set;
      }
    },
    toggleSupportDropdown() {
      this.showSupportDropdown = !this.showSupportDropdown;
    },
    async fetchPairingCode() {
      if (!this.isReady) return;
      const data = await vp.getPairingCode({
        mfg: this.mfg,
      });
      this.pairingCode = data.value;
      this.connectionsUrl = data.connectionsUrl;
      this.partnerToken = data.partnerToken;
    },
    submitDevice() {
      this.deviceData = {
        ...this.stepValues,
        pairingCode: this.pairingCode,
        mfg: this.mfg,
        houseId: this.houseId,
        program: this.programName,
        providerId: this.providerId,
      };
      return vp.addDevice(this.deviceData);
    },
    async chargepointLogin() {
      if (this.loginClicked) return window.open(this.connectionsUrl, '_blank');
      try {
        await this.submitDevice();
      } catch (error) {
        return this.$router.push('/error');
      }
      window.open(this.connectionsUrl, '_blank');
      this.$emit('input', this.pairingCode);
      this.loginClicked = true;
      return null;
    },
    onCopy() {
      this.pairingBtnTxt = this.$i18n.t('chargepointPairing.pairingCodeConfirmedText');
      this.codeCopied = true;
    },
    onCopyError() {
      this.pairingBtnTxt = this.$i18n.t('chargepointPairing.pairingCodeFailureText');
    },
    handleSubmit() {
      this.$emit('device-created', true);
      this.$router.push({ path: '/success', params: { type: 'chargepoint' } });
    },
    hexToRgba(hex, alpha) {
      let c = hex.replace('#', '');
      if (c.length === 3) {
        c = c.split('').map(x => x + x).join('');
      }
      const r = parseInt(c.slice(0, 2), 16);
      const g = parseInt(c.slice(2, 4), 16);
      const b = parseInt(c.slice(4, 6), 16);
      return `rgba(${r},${g},${b},${alpha})`;
    },
  },
};
</script>

<style lang="scss">
@import '../../styles/mixins.scss';
html {
  font-size: 16px;
}
.cp-pairing__container {
  max-width: 600px;
  margin: 40px auto;
  color: #333;

  .cp-pairing__title {
    font-size: 40px;
    margin-bottom: 32px;
    @media (max-width: 576px) {
      text-align: center;
    }
  }

  .cp-pairing__steps {
    position: relative;

    .cp-pairing__step {
      display: flex;

      .cp-pairing__step__indicator-container {
        width: 40px;
        position: relative;
        display: flex;
        flex-direction: column;
        align-items: center;

        [class^='cp-pairing__step__number-'] {
          width: 30px;
          height: 30px;
          line-height: 30px;
          font-size: 12px;
          font-weight: bold;
          z-index: 1;
        }
        .cp-pairing__step__number-filled {
          color: white;
          border-radius: 50%;
          text-align: center;
        }
        .cp-pairing__step__number-empty {
          border-radius: 50%;
          border: 2px solid;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .cp-pairing__step__number-grey {
          color: white;
          background-color: #BDBDBD;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .cp-pairing__step__line {
          width: 2px;
          flex-grow: 1;
          background-image: repeating-linear-gradient(
            to bottom,
            #ccc,
            #ccc 4px,
            transparent 4px,
            transparent 8px
          );
          margin-top: 18px;
          margin-bottom: 18px;
        }
      }

      .cp-pairing__step__content {
        flex: 1;
        margin-top: 4px;
        margin-left: 8px;

        .cp-pairing__step__title {
          font-weight: bold;
          color: black;
          font-size: 16px;
          line-height: 16px;
          margin-bottom: 0;
        }
        .cp-pairing__step__description {
          font-size: 12px;
          line-height: 16px;
          margin: 4px 0 10px;
        }

        #cp-pairing__code__container {
          display: flex;
          align-items: center;
          justify-content: center;
          border: none;
          box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.08);
          padding: 10px 10px;
          border-radius: 0 0 10px 10px;
          height: 60px;
          width: 284px;
          margin-top: 16px;

          .cp-pairing__code {
            font-size: 24px;
            margin: auto;
          }
          .cp-pairing__code__button {
            background: none;
            border: none;
            cursor: pointer;
            font-size: 16px;
            font-weight: bold;
            margin: auto;
            padding: 0px;
          }
        }

        .cp-pairing__step__support-info {
          display: flex;
          flex-direction: column;

          .cp-pairing__step__support-info__toggle {
            display: flex;
            align-items: center;
            cursor: pointer;
            font-size: 14px;
            margin-top: 12px;

            .cp-pairing__step__support-info__text-toggle {
              color: #4F4F4F;
              font-size: 12px;
              text-decoration: underline;
              margin: 0 6px 0 0;
            }
            #support-info__icon {
              height: 6px;
              width: 10px;
              align-self: flex-start;
              margin-top: 6px;
            }
          }

          .cp-pairing__step__support-info__content {
            display: flex;
            flex-direction: row;
            border: 1px solid #d1d5db;
            border-radius: 8px;
            margin-top: 16px;

            .cp-pairing__step__support-info__icon__container {
              display: flex;
              align-items: center;
              justify-content: center;
              flex: 0 0 auto;
              width: 16px;
              height: 16px;
              margin: auto 12px;

              #support-info__icon__attention {
                height: 100%;
                width: 100%;
                display: block;
              }
            }

            .cp-pairing__support-info__text {
              flex: 1 1 0%;
              min-width: 0;
              align-self: center;
              display: flex;
              flex-direction: column;
              justify-content: center;
              height: 100%;
              padding: 12px;

              p,
              span {
                color: #1B3664;
                font-size: 12px;
                margin: 0;
                padding: 0;
                line-height: 16px;
              }
              .partner-token-bold {
                font-weight: bold;
              }
              .cp-pairing__step__support-modal__text-toggle {
                font-weight: bold;
                cursor: pointer;
                text-decoration: underline;
              }
            }
          }
        }

        .buffer-line {
          height: 40px;
          margin-top: 15px;
          margin-bottom: 5px;
          width: 100%;
        }
      }
    }
  }
}

// Buttons and links
[class^='chargepoint-link-'],
[class^='submit-button-'] {
  display: inline-block;
  font-size: 14px;
  font-weight: bold;
  letter-spacing: 1px;
  border-radius: 4px;
  text-decoration: none;
  background: #fff;
  width: 224px;
  height: auto;
  box-sizing: border-box;
  padding: 0;
  margin-top: 14px;
}
.chargepoint-link-grey:disabled,
.chargepoint-link-grey[disabled] {
  background: #fff !important;
  cursor: not-allowed;
  opacity: 1;
}
.chargepoint-link-grey {
  color: #BDBDBD;
  border: 2px solid #AFAFAF;
}
.chargepoint-link-colored {
  border: 2px solid;
}
.submit-button-grey:disabled,
.submit-button-grey[disabled] {
  background: #BDBDBD !important;
  cursor: not-allowed;
  opacity: 1;
}
.submit-button-grey {
  color: #fff;
  background: #AFAFAF;
}
.submit-button-colored {
  color: #fff;
  background: #4CC68B;
}
</style>
